import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, Search, ChevronDown, FileText } from 'lucide-react';
import Dropdown from '../../../core/components/dashboard/Dropdown/Dropdown';
import Calendar from '../../../core/components/dashboard/Calendar/Calendar';
import { getPointService } from '../../../domains/point';
import { formatAmount, formatDateTime } from '../shared/dummyData';
import styles from './PointHistory.module.css';

const TABS = [
  { key: 'all', label: '전체' },
  { key: 'EARN', label: '적립' },
  { key: 'USE', label: '사용' },
  { key: 'CANCEL', label: '취소' },
];

const TYPE_LABELS = {
  EARN: '적립',
  USE: '사용',
  CANCEL: '취소',
  EXPIRE: '만료',
};

const TYPE_STYLE = {
  EARN: 'statusEarn',
  USE: 'statusUse',
  CANCEL: 'statusCancelled',
  EXPIRE: 'statusExpire',
};

const PERIOD_OPTIONS = [
  { key: 'last7days', label: '최근 7일' },
  { key: 'last30days', label: '최근 30일' },
  { key: 'last3months', label: '최근 3개월' },
  { key: 'custom', label: '직접 입력' },
];

const ITEMS_PER_PAGE = 10;

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const PointHistory = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [userId, setUserId] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [periodFilter, setPeriodFilter] = useState('last7days');
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    return { start, end };
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const tabRefs = useRef({});
  const calendarRef = useRef(null);

  // API 데이터
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [balance, setBalance] = useState(null);

  // Sliding indicator (pill style)
  useEffect(() => {
    const activeRef = tabRefs.current[activeTab];
    if (activeRef) {
      setIndicatorStyle({
        width: `${activeRef.offsetWidth}px`,
        transform: `translateX(${activeRef.offsetLeft}px)`,
      });
    }
  }, [activeTab]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, periodFilter, dateRange]);

  // Calendar outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  // API 호출
  const fetchTransactions = useCallback(async (uid) => {
    if (!uid) return;
    try {
      setIsLoading(true);
      setErrorMessage('');
      const pointService = getPointService();

      const params = {
        limit: 200,
        offset: 0,
      };
      if (activeTab !== 'all') {
        params.transaction_type = activeTab;
      }

      const response = await pointService.getTransactions(uid, params);

      if (response?.result_code === '200') {
        setTransactions(response.data?.items || []);
        setTotalCount(response.data?.total || 0);
      } else {
        setErrorMessage(response?.result_msg || '거래내역 조회에 실패했습니다.');
        setTransactions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('거래내역 조회 실패:', error);
      setErrorMessage(error.message || '거래내역 조회에 실패했습니다.');
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  // 잔액 조회
  const fetchBalance = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const pointService = getPointService();
      const response = await pointService.getBalance(uid);
      if (response?.result_code === '200') {
        setBalance(response.data);
      }
    } catch (error) {
      console.error('잔액 조회 실패:', error);
    }
  }, []);

  // 사용자 ID로 검색
  const handleSearch = () => {
    const uid = userId.trim();
    if (!uid) {
      setErrorMessage('사용자 ID를 입력해주세요.');
      return;
    }
    setSearchUserId(uid);
    setCurrentPage(1);
    fetchTransactions(uid);
    fetchBalance(uid);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 탭 변경 시 재조회
  useEffect(() => {
    if (searchUserId) {
      fetchTransactions(searchUserId);
    }
  }, [activeTab, searchUserId, fetchTransactions]);

  // 기간 필터링 (클라이언트 사이드)
  const filteredData = useMemo(() => {
    let data = [...transactions];

    const now = new Date();
    if (periodFilter === 'last7days') {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      data = data.filter((d) => new Date(d.created_at || d.timestamp) >= start);
    } else if (periodFilter === 'last30days') {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      data = data.filter((d) => new Date(d.created_at || d.timestamp) >= start);
    } else if (periodFilter === 'last3months') {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 3);
      data = data.filter((d) => new Date(d.created_at || d.timestamp) >= start);
    } else if (periodFilter === 'custom' && dateRange.start && dateRange.end) {
      const start = new Date(dateRange.start);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
      data = data.filter((d) => {
        const dt = new Date(d.created_at || d.timestamp);
        return dt >= start && dt <= end;
      });
    }

    return data;
  }, [transactions, periodFilter, dateRange]);

  // KPI
  const kpi = useMemo(() => {
    const total = filteredData.length;
    const earnTotal = filteredData
      .filter((d) => d.transaction_type === 'EARN')
      .reduce((sum, d) => sum + (d.amount || 0), 0);
    const useTotal = filteredData
      .filter((d) => d.transaction_type === 'USE')
      .reduce((sum, d) => sum + (d.amount || 0), 0);
    return { total, earnTotal, useTotal };
  }, [filteredData]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE));
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePeriodFilterChange = (filterKey) => {
    setPeriodFilter(filterKey);
    if (filterKey !== 'custom') {
      const end = new Date();
      const start = new Date();
      switch (filterKey) {
        case 'last7days':
          start.setDate(end.getDate() - 7);
          break;
        case 'last30days':
          start.setDate(end.getDate() - 30);
          break;
        case 'last3months':
          start.setMonth(end.getMonth() - 3);
          break;
        default:
          break;
      }
      setDateRange({ start, end });
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setIsCalendarOpen(false);
    setPeriodFilter('custom');
  };

  const periodDropdownOptions = PERIOD_OPTIONS
    .filter(option => option.key !== 'custom')
    .map(option => ({
      label: option.label,
      onClick: () => handlePeriodFilterChange(option.key),
    }));

  return (
    <div className={styles.pageContainer}>
        {/* 사용자 ID 검색 */}
        <div className={styles.userSearchRow}>
          <div className={styles.userSearchWrapper}>
            <label className={styles.userSearchLabel}>사용자 ID</label>
            <div className={styles.userSearchInput}>
              <input
                type="number"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="사용자 ID를 입력하세요"
              />
              <button onClick={handleSearch} disabled={isLoading}>
                {isLoading ? '조회중...' : '조회'}
              </button>
            </div>
          </div>
          {balance && (
            <div className={styles.balanceInfo}>
              <div className={styles.balanceItem}>
                <span className={styles.balanceLabel}>가용 포인트</span>
                <span className={styles.balanceValue}>{formatAmount(balance.available_points || 0)}P</span>
              </div>
              <div className={styles.balanceItem}>
                <span className={styles.balanceLabel}>총 적립</span>
                <span className={styles.balanceValueSub}>{formatAmount(balance.total_points || 0)}P</span>
              </div>
              <div className={styles.balanceItem}>
                <span className={styles.balanceLabel}>총 사용</span>
                <span className={styles.balanceValueSub}>{formatAmount(balance.used_points || 0)}P</span>
              </div>
            </div>
          )}
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}

        {/* KPI Row */}
        {searchUserId && (
          <div className={styles.creditInfoRow}>
            <div className={styles.creditBox}>
              <div className={styles.moneyRow}>
                <div className={styles.totalMoney}>
                  <div className={styles.boxLabel}>조회 건수</div>
                  <div className={styles.boxTotalValue}>{kpi.total}건</div>
                </div>
                <div className={styles.verticalDivider}></div>
                <div className={styles.moneyDetails}>
                  <div className={styles.moneyTypeRow}>
                    <span className={styles.boxLabel}>적립 합계</span>
                    <span className={styles.boxValue}>{formatAmount(kpi.earnTotal)}P</span>
                  </div>
                  <div className={styles.moneyTypeRow}>
                    <span className={styles.boxLabel}>사용 합계</span>
                    <span className={styles.boxValue}>{formatAmount(kpi.useTotal)}P</span>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.creditBox}>
              <div className={styles.boxLabel}>가용 포인트</div>
              <div className={styles.boxTotalValue}>{formatAmount(balance?.available_points || 0)}P</div>
            </div>
            <div className={styles.creditBox}>
              <div className={styles.boxLabel}>전체 거래</div>
              <div className={styles.boxTotalValue}>{totalCount}건</div>
            </div>
          </div>
        )}

        {/* History Section */}
        <div className={styles.historySection}>
          {/* Tab Navigation (pill style) */}
          <div className={styles.tabs}>
            <div className={styles.tabIndicator} style={indicatorStyle}></div>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                ref={(el) => (tabRefs.current[tab.key] = el)}
                className={`${styles.tab} ${activeTab === tab.key ? styles.activeTab : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filter Bar */}
          <div className={styles.searchFilterRow}>
            <div style={{ flex: 1 }} />
            <div className={styles.filterBar}>
              <Dropdown
                trigger={
                  <button className={styles.select}>
                    {PERIOD_OPTIONS.find(opt => opt.key === periodFilter)?.label}
                    <ChevronDown className={styles.dropdownArrow} />
                  </button>
                }
                options={periodDropdownOptions}
                align="left"
                minWidth="120px"
              />
              <div className={styles.dateRangeWrapper} ref={calendarRef}>
                <div
                  className={styles.dateRangeInput}
                  onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                >
                  <span>{formatDate(dateRange.start)}</span>
                  <span className={styles.dateSeparator}>~</span>
                  <span>{formatDate(dateRange.end)}</span>
                </div>
                {isCalendarOpen && (
                  <div className={styles.calendarDropdown}>
                    <Calendar
                      mode="range"
                      value={dateRange}
                      onChange={handleDateRangeChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>일시</th>
                  <th>유형</th>
                  <th>금액</th>
                  <th>설명</th>
                  <th>승인번호</th>
                </tr>
              </thead>
              <tbody>
                {!searchUserId ? (
                  <tr>
                    <td colSpan={5} className={styles.emptyState}>
                      <Search className={styles.emptyIcon} />
                      <div className={styles.emptyText}>사용자 ID를 입력하여 거래내역을 조회하세요</div>
                    </td>
                  </tr>
                ) : isLoading ? (
                  <tr>
                    <td colSpan={5} className={styles.emptyState}>
                      <div className={styles.emptyText}>조회 중...</div>
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((item, idx) => (
                    <tr
                      key={item.id || idx}
                      className={styles.clickableRow}
                      onClick={() => setSelectedItem(item)}
                    >
                      <td>{formatDateTime(item.created_at || item.timestamp)}</td>
                      <td>
                        <span
                          className={`${styles.statusBadge} ${
                            styles[TYPE_STYLE[item.transaction_type]] || styles.statusComplete
                          }`}
                        >
                          {TYPE_LABELS[item.transaction_type] || item.transaction_type}
                        </span>
                      </td>
                      <td className={styles.amount}>
                        {item.transaction_type === 'USE' || item.transaction_type === 'CANCEL'
                          ? `-${formatAmount(item.amount || 0)}`
                          : `+${formatAmount(item.amount || 0)}`}P
                      </td>
                      <td>{item.description || '-'}</td>
                      <td className={styles.approvalNumber}>{item.approval_number || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className={styles.emptyState}>
                      <FileText className={styles.emptyIcon} />
                      <div className={styles.emptyText}>해당 조건의 거래내역이 없습니다</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredData.length > 0 && (
            <div className={styles.paginationBar}>
              <div className={styles.paginationInfo}>
                {ITEMS_PER_PAGE}개씩 결과
              </div>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  ‹
                </button>
                <button className={`${styles.pageBtn} ${styles.activePage}`}>
                  {currentPage}
                </button>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedItem && (
          <div className={styles.modalOverlay} onClick={() => setSelectedItem(null)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>거래 상세</h2>
                <button className={styles.closeButton} onClick={() => setSelectedItem(null)}>
                  <X size={20} />
                </button>
              </div>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>거래 유형</div>
                  <div className={styles.detailValue}>
                    {TYPE_LABELS[selectedItem.transaction_type] || selectedItem.transaction_type}
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>금액</div>
                  <div className={styles.detailValue}>{formatAmount(selectedItem.amount || 0)}P</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>일시</div>
                  <div className={styles.detailValue}>
                    {formatDateTime(selectedItem.created_at || selectedItem.timestamp)}
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>승인번호</div>
                  <div className={styles.detailValue}>{selectedItem.approval_number || '-'}</div>
                </div>
                {selectedItem.description && (
                  <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                    <div className={styles.detailLabel}>설명</div>
                    <div className={styles.detailValue}>{selectedItem.description}</div>
                  </div>
                )}
                {selectedItem.business_number && (
                  <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                    <div className={styles.detailLabel}>사업자번호</div>
                    <div className={styles.detailValue}>{selectedItem.business_number}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default PointHistory;
